import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosRequestHeaders,
} from "axios";
import {
  CTM_BASE_URL,
  SENSZ_AI_BO_BASE_URL,
  SENSZ_AI_CLIENT_BASE_URL,
} from "./UrlConfigs"; // Import the base URL configurations
import { ApiErrorResponse } from "../Models/Models"; // Import your ApiErrorResponse model
import { showToast } from "../CommonComponents/ToastBox"; // Import your ToastBox component

// Function to create an Axios instance
const createHttpClient = (baseURL: string) => {
  if (!baseURL) {
    throw new Error("Base URL is required for HttpClient");
  }

  const httpClient = axios.create({
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    timeout: 60 * 1000,
    validateStatus(status: number) {
      return status >= 200 && status < 300;
    },
  });

  // Request Interceptor
  httpClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const accessToken = sessionStorage.getItem("accessToken");
        const xToken = sessionStorage.getItem("X-Token");
        const getPath = config?.url?.split("?") || [];
        const loginOrMemberUrls = ["/user/login"];
        const isLoginRequest = loginOrMemberUrls.some((url) =>
          getPath.includes(url)
        );

        config.headers = config.headers || ({} as AxiosRequestHeaders);
        if (isLoginRequest) {
          config.headers.Authorization = "Basic c2Vuc3p1c2VyOlNlbnN6QUktMjAyNA"; // Basic Auth for login
        } else if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`; // Bearer Auth
        }
        if (xToken) {
          config.headers["x-token"] = xToken; // Additional header
        }

        return config;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor
  httpClient.interceptors.response.use(
    function (response: AxiosResponse) {
      return response;
    },
    function (error: AxiosError<ApiErrorResponse>) {
      const status = error?.response?.status || 0;
      handleApiError(error);
      if (status === 401) {
        const requestPath = error?.config?.url || "";
        const loginUrls = ["/user/login"];
        const isLoginRequest = loginUrls.some((url) =>
          requestPath.includes(url)
        );
        if (!isLoginRequest) {
          window.location.href = "/"; // Redirect to login page
        }
      }

      return Promise.reject(error);
    }
  );

  return httpClient; // Return the configured httpClient
};

const handleApiError = (error: unknown) => {
  const axiosError = error as AxiosError<ApiErrorResponse>;
  const { response } = axiosError;

  if (response) {
    const status = response.status;
    if (status === 500 || status === undefined) {
      showToast("Something went Wrong", "error");
    } else {
      const errorMessage =
        response.data?.results?.[0] || "Something went Wrong";
      showToast(errorMessage, "error");
    }
  } else {
    showToast("Something went Wrong", "error");
  }
};

const SenszBoServices = createHttpClient(SENSZ_AI_BO_BASE_URL);
const SenszClientServices = createHttpClient(SENSZ_AI_CLIENT_BASE_URL);
const CtmServices = createHttpClient(CTM_BASE_URL);

export { SenszBoServices, SenszClientServices, CtmServices };
